import { useState } from 'react'
import { Box, Button, findBy, H5, makeStyles } from '@perk-ui/core'
import { ExternalLink, Mail, MessageCircle } from 'react-feather'

import { Patient } from '../features/query-hooks/usePatient'
import usePatientCases from '../features/query-hooks/usePatientCases'
import useResendSurveyNotification from '../features/query-hooks/useResendSurveyNotification'
import { SurveyResponseGroup } from '../features/query-hooks/useSurveyResponseGroups'
import { useCurrentSite } from './SitesProvider'
import SurveyCompletionModal from './SurveyCompletionModal'

export interface PatientCDOpenSurveysProps {
  openSurveys: SurveyResponseGroup[]
  patient?: Patient
  currentPeriod?: string
  parentToggle?: () => void
}

const useStyles = makeStyles((theme) => ({
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0, // Prevent flex item from growing beyond container
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    overflow: 'visible',
  },
  openSurveyButton: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    borderRadius: '4px',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  resendButton: {
    marginBottom: theme.spacing(1),
    justifyContent: 'center',
    textTransform: 'none',
    fontSize: '14px',
    padding: theme.spacing(0.5, 1),
    minHeight: 'auto',
  },
  resendEmailButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  resendSmsButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
}))

const PatientCDOpenSurveys: React.FC<PatientCDOpenSurveysProps> = ({
  openSurveys,
  patient,
  currentPeriod,
  parentToggle,
}) => {
  const classes = useStyles()
  const resendSurveyNotification = useResendSurveyNotification()
  const { data: patientCases = [] } = usePatientCases(patient?.id || '')
  const currentSite = useCurrentSite()
  const siteOrganization = currentSite.organization

  const [surveyModalOpen, setSurveyModalOpen] = useState(false)
  const [selectedSurvey, setSelectedSurvey] =
    useState<SurveyResponseGroup | null>(null)

  const getSurveyContext = (openSurvey: SurveyResponseGroup): string => {
    const patientCase = findBy('id', openSurvey.firstCaseId, patientCases)
    const bodySite = patientCase?.bodySite

    // Determine time period based on survey availability date
    // This is a simplified approach - in a real implementation,
    // you would need to map the survey timing to proper period labels
    // const now = new Date()
    // const availableDate = new Date(openSurvey.availableOn)
    // const daysSinceAvailable = Math.floor(
    //   (now.getTime() - availableDate.getTime()) / (1000 * 60 * 60 * 24),
    // )

    // let timePeriod = 'Baseline'
    // if (daysSinceAvailable > 365) {
    //   timePeriod = '12 Month'
    // } else if (daysSinceAvailable > 180) {
    //   timePeriod = '6 Month'
    // } else if (daysSinceAvailable > 90) {
    //   timePeriod = '3 Month'
    // } else if (daysSinceAvailable > 30) {
    //   timePeriod = '1 Month'
    // } else if (daysSinceAvailable > 14) {
    //   timePeriod = '2 Week'
    // }

    // Only include body site if it exists, otherwise just show time period
    return bodySite ? `${bodySite} - ${currentPeriod}` : currentPeriod || ''
  }

  const handleOpenSurveyModal = (openSurvey: SurveyResponseGroup) => {
    setSelectedSurvey(openSurvey)
    setSurveyModalOpen(true)
    if (parentToggle) {
      parentToggle()
    }
  }

  const handleCloseSurveyModal = () => {
    setSurveyModalOpen(false)
    setSelectedSurvey(null)
  }

  const handleContinueInBrowser = () => {
    if (selectedSurvey && selectedSurvey.shortLink) {
      const patientCase = findBy('id', selectedSurvey.firstCaseId, patientCases)
      const treatmentProductManufacturer =
        patientCase?.patientTreatments?.[0]?.treatmentProduct?.manufacturer ||
        ''

      const surveyUrl =
        selectedSurvey.shortLink +
        `?comm_channel=manual&org=${siteOrganization.name}&manufacturer=${treatmentProductManufacturer}`

      window.open(surveyUrl, '_blank')
    }
  }

  const handleDirectSurveyAccess = (openSurvey: SurveyResponseGroup) => {
    if (openSurvey.shortLink) {
      const patientCase = findBy('id', openSurvey.firstCaseId, patientCases)
      const treatmentProductManufacturer =
        patientCase?.patientTreatments?.[0]?.treatmentProduct?.manufacturer ||
        ''

      const surveyUrl =
        openSurvey.shortLink +
        `?comm_channel=manual&org=${siteOrganization.name}&manufacturer=${treatmentProductManufacturer}`

      window.open(surveyUrl, '_blank')
    }
  }

  const handleSmsClick = (surveyResponseGroupId: string) => {
    return resendSurveyNotification.mutateAsync({
      surveyResponseGroupId: surveyResponseGroupId,
      medium: 'sms',
    })
  }
  const handleEmailClick = (surveyResponseGroupId: string) => {
    return resendSurveyNotification.mutateAsync({
      surveyResponseGroupId: surveyResponseGroupId,
      medium: 'email',
    })
  }

  let isSmsLinkDisabled = false
  let isEmailLinkDisabled = false
  if (patient) {
    if (patient.isOptedOutPhone || !patient.isValidPhone) {
      isSmsLinkDisabled = true
    }
    if (!patient.email) {
      isEmailLinkDisabled = true
    }
  }
  // Use first survey for actions (since modal context is already filtered)
  const firstSurvey = openSurveys[0]

  const handleOpenSurveyClick = () => {
    if (firstSurvey) {
      if (currentSite?.hasQrCode) {
        handleOpenSurveyModal(firstSurvey)
      } else {
        handleDirectSurveyAccess(firstSurvey)
      }
    }
  }

  const handleEmailResend = () => {
    if (firstSurvey) {
      handleEmailClick(firstSurvey.id)
    }
  }

  const handleSmsResend = () => {
    if (firstSurvey) {
      handleSmsClick(firstSurvey.id)
    }
  }

  return (
    <Box className={classes.actionContainer}>
      <H5>Actions</H5>
      <Box className={classes.actionsContainer}>
        <Button
          variant="contained"
          size="small"
          className={classes.openSurveyButton}
          onClick={handleOpenSurveyClick}
          disabled={!firstSurvey?.shortLink}
          endIcon={<ExternalLink size={16} />}
        >
          {currentSite?.hasQrCode
            ? 'Open Patient Surveys'
            : 'Complete Manually'}
        </Button>

        <Button
          variant="text"
          fullWidth
          className={`${classes.resendButton} ${classes.resendEmailButton}`}
          onClick={handleEmailResend}
          disabled={
            isEmailLinkDisabled ||
            resendSurveyNotification.isLoading ||
            !firstSurvey
          }
          endIcon={<Mail size={16} />}
        >
          Resend via Email
        </Button>

        <Button
          variant="text"
          fullWidth
          className={`${classes.resendButton} ${classes.resendSmsButton}`}
          onClick={handleSmsResend}
          disabled={
            isSmsLinkDisabled ||
            resendSurveyNotification.isLoading ||
            !firstSurvey
          }
          endIcon={<MessageCircle size={16} />}
        >
          Resend via SMS
        </Button>
      </Box>

      <SurveyCompletionModal
        open={surveyModalOpen}
        onClose={handleCloseSurveyModal}
        surveyUrl={
          selectedSurvey?.shortLink
            ? selectedSurvey.shortLink +
              `?comm_channel=manual&org=${siteOrganization.name}&manufacturer=${
                findBy('id', selectedSurvey.firstCaseId, patientCases)
                  ?.patientTreatments?.[0]?.treatmentProduct?.manufacturer || ''
              }`
            : ''
        }
        surveyContext={selectedSurvey ? getSurveyContext(selectedSurvey) : ''}
        patientId={patient?.id || ''}
        onContinueInBrowser={handleContinueInBrowser}
      />
    </Box>
  )
}

export default PatientCDOpenSurveys
